<template>
  <div class="d-flex flex-column">
    <div
      v-if="['idle', 'progress'].includes(emailUpdateStatus)"
      class="pa-0 text-black mb-4"
    >
      {{
        globalStore.selectedSignee?.status === SignStatus.ON_HOLD
          ? i18n.t('texts.update_signee_on_hold_email_hint')
          : i18n.t('texts.update_signee_email_hint')
      }}
    </div>
    <v-form
      v-if="['idle', 'progress'].includes(emailUpdateStatus)"
      ref="editEmailForm"
      @submit.prevent="sendInvitation"
    >
      <v-text-field
        key="invitation-email-input"
        v-model.trim="email"
        v-no-password-manager
        autocapitalize="off"
        autocomplete="off"
        autocorrect="off"
        autofocus
        class="mt-2 bg-white"
        clear-icon="fal fa-circle-xmark"
        clearable
        data-test-id="update-email-input"
        density="compact"
        :label="i18n.t('labels.email')"
        :rules="[ruleEmail]"
        type="email"
        variant="outlined"
      />
    </v-form>

    <v-alert
      v-if="emailUpdateStatus === 'error'"
      class="mb-5 text-body-small"
      data-test-id="deep-sign-alert"
      type="error"
      variant="tonal"
    >
      <div class="text-body-medium">
        {{ i18n.t('errors.generic') }}
      </div>
    </v-alert>

    <portal to="document-dialog-actions">
      <VBtnSecondary variant="outlined" @click="closeDialog">
        {{
          i18n.t(
            emailUpdateStatus === 'error' ? 'buttons.close' : 'buttons.cancel'
          )
        }}
      </VBtnSecondary>
      <VBtnPrimary
        v-if="emailUpdateStatus !== 'error'"
        color="primary"
        data-test-id="send-invitation"
        :disabled="!isFormValid"
        @click="sendInvitation"
        >{{
          globalStore.selectedSignee?.status === SignStatus.ON_HOLD
            ? i18n.t('buttons.update_email')
            : i18n.t('buttons.send_invitation')
        }}
      </VBtnPrimary>
    </portal>
  </div>
</template>

<script setup lang="ts">
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useEmitter } from '@/composables/useEmitter';
import { useGlobalStore } from '@/stores/global.ts';
import { useSignStore } from '@/stores/sign.ts';
import { SignStatus } from '@/types/enums/SignStatus';
import type { ComponentState } from '@/types/ui';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ruleEmail } from '@deepcloud/field-validators';
import { vNoPasswordManager } from '@deepcloud/deep-ui-lib';
const i18n = useI18n();
const editEmailForm = ref();
const signStore = useSignStore();
const globalStore = useGlobalStore();
const emit = defineEmits(['closeDialog']);
const email = ref<string | null>(globalStore.selectedSignee?.email || null);
const emailUpdateStatus = ref<ComponentState>('idle');
const emitter = useEmitter();
const { currentDocument } = useDocumentContext();

const isFormValid = computed(() => {
  if (!editEmailForm.value || email.value === null) return false;
  return editEmailForm.value.isValid;
});

function closeDialog() {
  email.value = null;
  emit('closeDialog');
}

async function sendInvitation() {
  try {
    emailUpdateStatus.value = 'progress';
    if (
      globalStore.selectedSignee?.signeeId &&
      email.value &&
      isFormValid &&
      currentDocument.value
    ) {
      await signStore.updateSigneeEmail(
        currentDocument.value.documentId,
        globalStore.selectedSignee.signeeId,
        { email: email.value }
      );
      emailUpdateStatus.value = 'success';
      closeDialog();
      emitter.$emit('update-sign-status');
    }
    emailUpdateStatus.value = 'idle';
  } catch (error) {
    emailUpdateStatus.value = 'error';
  }
}
</script>

<style scoped lang="scss">
:deep(.v-text-field.v-text-field--enclosed) .v-text-field__details {
  padding-left: 0 !important;
}

.v-input :deep(.v-input__details) {
  padding: 0;
  min-height: 18px;
}

.v-input.v-text-field {
  margin-bottom: 4px;
}
</style>
